import React, { useEffect, useMemo, useRef, useState } from "react";
import { simulationAPI, strategyAPI } from "../services/api";
import { fmtDate } from "../utils/date";
import { legCharges } from "../utils/charges";
import { formatOptionSymbol } from "../utils/optionSymbol";

const RANGES = ["1 Month", "3 Months", "6 Months", "1 Year", "2 Years", "Custom Range"];
const SPEEDS = [["1x", 600], ["2x", 300], ["5x", 120]];
const SESSION_KEY = "sim_session_v1";

const UI = {
  bg: "#eef2f7", card: "#fff", border: "#e8eaf0", text: "#0f172a",
  sub: "#64748b", muted: "#9ca3af", blue: "#2563eb", green: "#10b981", red: "#ef4444",
};
const card = (extra) => ({ background: "#fff", border: "1px solid #e8eaf0", borderRadius: 12, padding: "18px 20px", marginBottom: 16, ...extra });
const inr = (n) => `${n >= 0 ? "+" : "-"}₹${Math.abs(Math.round(n || 0)).toLocaleString()}`;

// Human-readable label + colour for why a position closed. The engine records a
// raw exitType (SL HIT | TARGET | SQUAREOFF | OVERALL SL | OVERALL TARGET |
// INDICATOR EXIT | EXPIRY | ROLL); map it to something a trader reads at a glance.
const EXIT_REASONS = {
  "SL HIT":         { label: "Stop-loss hit",   bg: "#fee2e2", fg: "#b91c1c" },
  "TARGET":         { label: "Target hit",      bg: "#dcfce7", fg: "#15803d" },
  "SQUAREOFF":      { label: "Time square-off", bg: "#f1f5f9", fg: "#475569" },
  "OVERALL SL":     { label: "Overall SL hit",  bg: "#fee2e2", fg: "#b91c1c" },
  "OVERALL TARGET": { label: "Overall target",  bg: "#dcfce7", fg: "#15803d" },
  "INDICATOR EXIT": { label: "Indicator exit",  bg: "#ede9fe", fg: "#6d28d9" },
  "EXPIRY":         { label: "Expiry settled",  bg: "#fef9c3", fg: "#854d0e" },
  "ROLL":           { label: "Rolled",          bg: "#dbeafe", fg: "#1e40af" },
  "INSIDE BAR TRAIL":  { label: "Trail exit",      bg: "#cffafe", fg: "#0e7490" },
  "INSIDE BAR TARGET": { label: "IB target hit",   bg: "#dcfce7", fg: "#15803d" },
  "INSIDE BAR SL":     { label: "Inside-bar SL",   bg: "#fee2e2", fg: "#b91c1c" },
  "SL COST":           { label: "SL moved to cost", bg: "#fef3c7", fg: "#92400e" },
};
const exitReason = (t) => EXIT_REASONS[t] || (t ? { label: t, bg: "#f1f5f9", fg: "#475569" } : null);

// Mark an option leg to market at a given candle date+time, using its downloaded
// premium series (closes). Falls back to the entry price for legs that carry no
// series. Carry (NRML) series span MULTIPLE days, so each point has a `d` date —
// match by "date time" then. Single-day (MIS) series have no `d`: match by time.
function markLeg(e, date, time) {
  const s = e.series;
  if (!s || !s.length) return e.entryPrice;
  const multiDay = !!s[0].d;
  const target = multiDay ? `${date} ${time}` : time;
  let best = null;
  for (const p of s) {
    const pa = multiDay ? `${p.d} ${p.t}` : p.t;
    if (pa <= target) best = p; else break;
  }
  return best ? best.c : s[0].c;
}

// ── Black-Scholes |Δ| (mirrors the backtest engine) ─────────────────────────
// IV is implied from the leg's own premium at the moment, then delta from that IV.
// Lets the replay show each leg's LIVE delta ticking as you scrub (zero lookahead:
// open legs use the CURRENT candle's spot + mark, never a future value).
const _erf = (x) => {
  const t = 1 / (1 + 0.3275911 * Math.abs(x));
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
  return x >= 0 ? y : -y;
};
const _ncdf = (x) => 0.5 * (1 + _erf(x / Math.SQRT2));
const _R = 0.065;
const _bsPrice = (S, K, T, sig, isCall) => {
  const sd = sig * Math.sqrt(T);
  const d1 = (Math.log(S / K) + (_R + sig * sig / 2) * T) / sd, d2 = d1 - sd;
  return isCall ? S * _ncdf(d1) - K * Math.exp(-_R * T) * _ncdf(d2)
                : K * Math.exp(-_R * T) * _ncdf(-d2) - S * _ncdf(-d1);
};
const _bsDelta = (S, K, T, sig, isCall) => {
  const d1 = (Math.log(S / K) + (_R + sig * sig / 2) * T) / (sig * Math.sqrt(T));
  return isCall ? _ncdf(d1) : _ncdf(d1) - 1;
};
const _impliedVol = (price, S, K, T, isCall) => {
  if (!(price > 0) || !(S > 0) || !(K > 0) || !(T > 0)) return null;
  let lo = 0.01, hi = 5;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2, p = _bsPrice(S, K, T, mid, isCall);
    if (Math.abs(p - price) < 0.01) return mid;
    if (p > price) hi = mid; else lo = mid;
  }
  return (lo + hi) / 2;
};
// |Δ| of one leg given spot + its premium at a "YYYY-MM-DD" date and "HH:MM" time.
function legDeltaAt(spot, strike, optType, dateKey, hhmm, expiryStr, premium) {
  if (!spot || !strike || !(premium > 0) || !expiryStr || !dateKey) return null;
  const expMs = new Date(`${expiryStr}T15:30:00+05:30`).getTime();
  const atMs = new Date(`${dateKey}T${(hhmm || "09:15")}:00+05:30`).getTime();
  const T = Math.max((expMs - atMs) / (365 * 24 * 3600 * 1000), 1 / (365 * 24 * 60));
  const isCall = optType === "CE";
  const iv = _impliedVol(premium, spot, strike, T, isCall);
  if (!iv) return null;
  return Math.round(Math.abs(_bsDelta(spot, strike, T, iv, isCall)) * 1000) / 1000;
}

// Monday-start week key (YYYY-MM-DD of that week's Monday) for an IST date string.
// Used to group trades into expiry weeks (a NIFTY weekly expiry sits in one week).
function weekStart(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay(); // 0 Sun … 6 Sat
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day)); // back to Monday
  return d.toISOString().slice(0, 10);
}

// Monthly calendar of realized P&L by expiry. `weeks`: [{ expiry:'YYYY-MM-DD',
// pnl }]. Renders one month grid per month present; expiry days show their P&L
// (green/red). Months wrap to the next line, so the section stays on-screen.
function ExpiryCalendar({ weeks }) {
  if (!weeks.length) {
    return <div style={{ fontSize: 12, color: UI.muted }}>No expiry closed yet — play the replay forward.</div>;
  }
  const byDate = new Map(weeks.map(w => [w.expiry, w]));
  const monthMap = new Map();
  weeks.forEach(w => { const [y, m] = w.expiry.split("-"); monthMap.set(`${y}-${m}`, { year: +y, monthIdx: +m - 1 }); });
  const months = [...monthMap.values()].sort((a, b) => a.year - b.year || a.monthIdx - b.monthIdx);
  const WD = ["M", "T", "W", "T", "F", "S", "S"];

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
      {months.map(({ year, monthIdx }) => {
        const first = new Date(year, monthIdx, 1);
        const startDow = (first.getDay() + 6) % 7; // Monday-first offset
        const dim = new Date(year, monthIdx + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < startDow; i++) cells.push(null);
        for (let d = 1; d <= dim; d++) {
          const key = `${year}-${String(monthIdx + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const w = byDate.get(key);
          cells.push({ day: d, key, pnl: w ? w.pnl : undefined, iv: w ? w.iv : null });
        }
        const monthTot = cells.reduce((s, c) => s + (c && c.pnl != null ? c.pnl : 0), 0);
        const monthName = new Date(year, monthIdx, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
        return (
          <div key={`${year}-${monthIdx}`} style={{ flex: "1 1 280px", minWidth: 250, maxWidth: 420, border: "1px solid #eef2f7", borderRadius: 12, padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 13 }}>{monthName}</div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: monthTot >= 0 ? UI.green : UI.red }}>{inr(monthTot)}</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
              {WD.map((d, i) => <div key={`h${i}`} style={{ textAlign: "center", fontSize: 10, color: UI.muted, fontWeight: 700, paddingBottom: 2 }}>{d}</div>)}
              {cells.map((c, i) => {
                if (!c) return <div key={`e${i}`} />;
                const has = c.pnl != null;
                const up = (c.pnl || 0) >= 0;
                return (
                  <div key={c.key} title={has ? `${c.key}: ${inr(c.pnl)}${c.iv != null ? ` · ATM IV ${c.iv}%` : ""}` : c.key}
                    style={{ minHeight: 50, borderRadius: 7, padding: "3px 4px", background: has ? (up ? "#f0fdf4" : "#fef2f2") : "#fafbfc", border: `1px solid ${has ? (up ? "#bbf7d0" : "#fecaca") : "#f1f3f9"}` }}>
                    <div style={{ fontSize: 9.5, color: UI.muted, fontWeight: 600 }}>{c.day}</div>
                    {has && <div style={{ fontSize: 10, fontWeight: 800, color: up ? UI.green : UI.red, lineHeight: 1.15, overflow: "hidden", textOverflow: "ellipsis" }}>{inr(c.pnl)}</div>}
                    {has && c.iv != null && <div style={{ fontSize: 9, fontWeight: 700, color: "#7c3aed", lineHeight: 1.2, marginTop: 1 }}>IV {c.iv}%</div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Custom SVG candlestick chart ─────────────────────────────────────────────
// Renders ONLY the candles revealed so far (a trailing window), so future bars
// are never shown — zero lookahead. Entry/exit markers appear on the bar whose
// "YYYY-MM-DD HH:MM" key matches a trade event.
function CandleChart({ candles, idx, entryMap, exitMap }) {
  const W = 1000, H = 360, padT = 16, padB = 26, padL = 8, padR = 8;
  const DEFAULT_BARS = 130, MIN_BARS = 10, MAX_BARS = 500;
  const wrapRef = useRef(null);
  // Hovered candle: { i, mx, my } where mx/my are pixel coords inside the wrapper.
  const [hover, setHover] = useState(null);
  // Zoom: how many of the most-recent revealed candles to show. Fewer bars =
  // zoomed in (wider candles); more bars = zoomed out (more history on screen).
  const [visibleBars, setVisibleBars] = useState(DEFAULT_BARS);
  const zoom = (factor) => setVisibleBars(v => Math.max(MIN_BARS, Math.min(MAX_BARS, Math.round(v * factor))));
  const revealed = candles.slice(0, idx + 1);
  const chartReady = revealed.length > 0;
  // Mouse-wheel zoom: scroll up = zoom in (fewer bars), down = zoom out. Attached
  // imperatively with { passive: false } so preventDefault() actually stops the
  // page from scrolling — React's onWheel prop can't guarantee a non-passive
  // listener. Depends on chartReady so it (re)attaches once the wrapRef div
  // exists, not just on the initial "Press Play" placeholder where it is absent.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 0.85 : 1.18;
      setVisibleBars(v => Math.max(MIN_BARS, Math.min(MAX_BARS, Math.round(v * factor))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [chartReady]);
  const zbtn = (disabled) => ({
    width: 26, height: 26, borderRadius: 6, border: "1px solid #d6dae3",
    background: "#fff", color: UI.text, fontSize: 17, fontWeight: 700, lineHeight: 1,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)", opacity: disabled ? 0.4 : 1,
    cursor: disabled ? "default" : "pointer",
  });
  const win = revealed.slice(-visibleBars);
  if (!win.length) {
    return <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: UI.muted, fontSize: 13 }}>Press Play to start the replay…</div>;
  }
  let lo = Infinity, hi = -Infinity;
  for (const c of win) { if (c.l < lo) lo = c.l; if (c.h > hi) hi = c.h; }
  const span = hi - lo || 1;
  const colW = (W - padL - padR) / win.length;
  const x = (i) => padL + (i + 0.5) * colW;
  const y = (p) => padT + (hi - p) / span * (H - padT - padB);
  const bw = Math.max(1.5, colW * 0.6);
  const last = win[win.length - 1];

  // Day separators (vertical lines where the date changes).
  const seps = [];
  for (let i = 1; i < win.length; i++) if (win[i].date !== win[i - 1].date) seps.push(i);

  // Map a mouse event to the nearest candle. The SVG stretches to fill the
  // wrapper (preserveAspectRatio="none"), so x in pixels maps linearly to the
  // viewBox: viewBoxX = (px / wrapperWidth) * W.
  const onMove = (e) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    const vx = (mx / r.width) * W;
    let i = Math.floor((vx - padL) / colW);
    if (i < 0) i = 0; else if (i >= win.length) i = win.length - 1;
    setHover({ i, mx, my });
  };

  const hc = hover ? win[hover.i] : null;

  return (
    <div ref={wrapRef} style={{ position: "relative" }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      {/* Zoom controls — "+" shows fewer bars (zoom in), "−" shows more (zoom out). */}
      <div
        onMouseMove={(e) => e.stopPropagation()}
        style={{ position: "absolute", top: 8, right: 10, zIndex: 6, display: "flex", alignItems: "center", gap: 6 }}
      >
        <button type="button" title="Zoom out" onClick={() => zoom(1.25)}
          disabled={visibleBars >= MAX_BARS || visibleBars >= revealed.length}
          style={zbtn(visibleBars >= MAX_BARS || visibleBars >= revealed.length)}>−</button>
        <span style={{ fontSize: 11, color: UI.muted, fontVariantNumeric: "tabular-nums", minWidth: 50, textAlign: "center" }}>{win.length} bars</span>
        <button type="button" title="Zoom in" onClick={() => zoom(0.8)}
          disabled={visibleBars <= MIN_BARS}
          style={zbtn(visibleBars <= MIN_BARS)}>+</button>
      </div>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block" }}>
        {/* horizontal gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((f, k) => {
          const p = hi - f * span;
          return (
            <g key={k}>
              <line x1={padL} x2={W - padR} y1={y(p)} y2={y(p)} stroke="#f1f3f9" strokeWidth="1" />
              <text x={W - padR} y={y(p) - 2} textAnchor="end" fontSize="11" fill={UI.muted}>{Math.round(p)}</text>
            </g>
          );
        })}
        {seps.map((i, k) => <line key={`s${k}`} x1={x(i) - bw} x2={x(i) - bw} y1={padT} y2={H - padB} stroke="#eef2f7" strokeWidth="1" />)}
        {/* last price line */}
        <line x1={padL} x2={W - padR} y1={y(last.c)} y2={y(last.c)} stroke="#94a3b8" strokeDasharray="3 3" strokeWidth="1" />
        {/* crosshair on the hovered bar */}
        {hc && <line x1={x(hover.i)} x2={x(hover.i)} y1={padT} y2={H - padB} stroke="#cbd5e1" strokeWidth="1" />}
        {/* candles */}
        {win.map((c, i) => {
          const up = c.c >= c.o;
          const col = up ? UI.green : UI.red;
          const yO = y(c.o), yC = y(c.c);
          const bodyTop = Math.min(yO, yC), bodyH = Math.max(1, Math.abs(yC - yO));
          const cx = x(i);
          const ent = entryMap[c.key], ext = exitMap[c.key];
          return (
            <g key={i}>
              <line x1={cx} x2={cx} y1={y(c.h)} y2={y(c.l)} stroke={col} strokeWidth="1" />
              <rect x={cx - bw / 2} y={bodyTop} width={bw} height={bodyH} fill={col} />
              {ent && <text x={cx} y={H - padB - 1} textAnchor="middle" fontSize="13" fill={ent.some(s => s === "BUY") ? UI.green : UI.red}>▲</text>}
              {ext && <text x={cx} y={padT + 11} textAnchor="middle" fontSize="13" fill="#7c3aed">▼</text>}
            </g>
          );
        })}
      </svg>
      {/* Per-candle time axis — one label under every bar, rotated vertical so
          they all fit. Positioned as an HTML overlay (not SVG text) to avoid the
          horizontal distortion from preserveAspectRatio="none". */}
      <div style={{ position: "relative", height: 46, marginTop: 2 }}>
        {win.map((c, i) => (
          <span
            key={i}
            style={{
              position: "absolute",
              left: `${(x(i) / W) * 100}%`,
              top: 0,
              transform: "translateX(-50%)",
              writingMode: "vertical-rl",
              textOrientation: "mixed",
              fontSize: 8.5,
              color: UI.muted,
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {c.time}
          </span>
        ))}
      </div>
      {/* OHLC tooltip — follows the cursor, flips to the left near the right edge */}
      {hc && (
        <div style={{
          position: "absolute", top: Math.max(4, hover.my - 78),
          left: hover.mx > (wrapRef.current?.clientWidth || W) / 2 ? undefined : hover.mx + 14,
          right: hover.mx > (wrapRef.current?.clientWidth || W) / 2 ? (wrapRef.current?.clientWidth || W) - hover.mx + 14 : undefined,
          pointerEvents: "none", background: "rgba(15,23,42,0.94)", color: "#fff",
          borderRadius: 8, padding: "8px 11px", fontSize: 11.5, lineHeight: 1.55,
          boxShadow: "0 6px 18px rgba(0,0,0,0.25)", whiteSpace: "nowrap", zIndex: 5,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 3, color: "#cbd5e1" }}>{fmtDate(hc.date)} {hc.time}</div>
          <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: 14, rowGap: 1 }}>
            <span style={{ color: "#94a3b8" }}>O</span><span style={{ textAlign: "right", fontWeight: 600 }}>{Math.round(hc.o).toLocaleString()}</span>
            <span style={{ color: "#94a3b8" }}>H</span><span style={{ textAlign: "right", fontWeight: 600, color: UI.green }}>{Math.round(hc.h).toLocaleString()}</span>
            <span style={{ color: "#94a3b8" }}>L</span><span style={{ textAlign: "right", fontWeight: 600, color: UI.red }}>{Math.round(hc.l).toLocaleString()}</span>
            <span style={{ color: "#94a3b8" }}>C</span><span style={{ textAlign: "right", fontWeight: 700, color: hc.c >= hc.o ? UI.green : UI.red }}>{Math.round(hc.c).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}

const Metric = ({ label, value, color, sub }) => (
  <div style={{ background: "#fff", border: "1px solid #e8eaf0", borderRadius: 10, padding: "14px 16px" }}>
    <div style={{ fontSize: 11, color: UI.muted, fontWeight: 600, marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 19, fontWeight: 800, color: color || UI.text }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: UI.muted, marginTop: 3 }}>{sub}</div>}
  </div>
);

export default function SimulatedTrading() {
  const [strategies, setStrategies] = useState([]);
  const [strategyId, setStrategyId] = useState("");
  const [range, setRange] = useState("1 Month");
  const [intervalMin, setIntervalMin] = useState(5); // replay candle size (min)
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null); // { underlying, candles, events, summary, from, to }

  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(600);
  const [jumpDate, setJumpDate] = useState("");
  const [savedSession, setSavedSession] = useState(null);
  const resumeIdxRef = useRef(null);

  // Load strategies + any saved session selection.
  useEffect(() => {
    strategyAPI.getAll().then(r => setStrategies(r.data || [])).catch(() => setStrategies([]));
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        setSavedSession(s);
        if (s.strategyId) setStrategyId(s.strategyId);
        if (s.range) setRange(s.range);
        if (s.interval) setIntervalMin(s.interval);
        if (s.customFrom) setCustomFrom(s.customFrom);
        if (s.customTo) setCustomTo(s.customTo);
      }
    } catch { /* ignore */ }
  }, []);

  // Replay clock.
  useEffect(() => {
    if (!playing || !data) return;
    const t = setInterval(() => {
      setIdx(i => {
        if (i >= data.candles.length - 1) { setPlaying(false); return i; }
        return i + 1;
      });
    }, speed);
    return () => clearInterval(t);
  }, [playing, speed, data]);

  const prepare = async (resumeIdx = null) => {
    if (!strategyId) { setError("Select a strategy first."); return; }
    setLoading(true); setError(null); setData(null); setPlaying(false);
    try {
      const res = await simulationAPI.prepare({ strategyId, range, customFrom, customTo, interval: intervalMin });
      const d = res.data;
      if (!d.candles?.length) throw new Error("No candles returned for this range.");
      setData(d);
      const start = resumeIdx != null && resumeIdx < d.candles.length ? resumeIdx : 0;
      setIdx(start);
      setJumpDate(d.candles[0].date);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Could not prepare simulation.");
    }
    setLoading(false);
  };

  // Pre-compute the full per-candle equity / drawdown / open-position track ONCE
  // (so scrubbing the replay is cheap). realized P&L accrues as exits pass; open
  // legs are marked to market against their premium series.
  const prepared = useMemo(() => {
    if (!data) return null;
    const { candles, events } = data;
    // Bucket each event under EVERY calendar date it spans (entryDate..exitDate),
    // not just its entry date — otherwise a carry (NRML) position that is held
    // across days disappears from the open-position loop after day 1 and its P&L
    // stops updating. MIS legs span a single day, so this is unchanged for them.
    const dates = [...new Set(candles.map(c => c.date))];
    const byDate = {};
    events.forEach(e => {
      const ed = e.date, xd = e.exitKey.slice(0, 10);
      for (const d of dates) { if (d >= ed && d <= xd) (byDate[d] = byDate[d] || []).push(e); }
    });
    const byExit = [...events].sort((a, b) => (a.exitKey < b.exitKey ? -1 : 1));
    const entryMap = {}, exitMap = {};
    events.forEach(e => {
      (entryMap[e.entryKey] = entryMap[e.entryKey] || []).push(e.side);
      (exitMap[e.exitKey] = exitMap[e.exitKey] || []).push(e.side);
    });
    let ei = 0, realized = 0, peak = 0;
    const series = candles.map(cd => {
      const K = cd.key;
      while (ei < byExit.length && byExit[ei].exitKey <= K) { realized += (byExit[ei].pnl || 0); ei++; }
      let openMtm = 0; const open = [];
      for (const e of (byDate[cd.date] || [])) {
        if (e.entryKey <= K && K < e.exitKey) {
          const mark = markLeg(e, cd.date, cd.time);
          const m = (e.side === "SELL" ? (e.entryPrice - mark) : (mark - e.entryPrice)) * e.qty;
          openMtm += m;
          open.push({ symbol: e.symbol, side: e.side, qty: e.qty, entry: e.entryPrice, mark, mtm: Math.round(m), strike: e.strike, optType: e.optType });
        }
      }
      const pnl = realized + openMtm;
      if (pnl > peak) peak = pnl;
      return { key: K, date: cd.date, time: cd.time, pnl: Math.round(pnl), realized: Math.round(realized), openMtm: Math.round(openMtm), dd: Math.round(pnl - peak), open };
    });
    return { series, entryMap, exitMap };
  }, [data]);

  // Live dashboard (everything realized/visible up to the current candle).
  const live = useMemo(() => {
    if (!prepared || !data) return null;
    const cur = prepared.series[Math.min(idx, prepared.series.length - 1)] || prepared.series[0];
    const curKey = cur?.key;
    const closed = data.events.filter(e => e.exitKey <= curKey);
    const wins = closed.filter(e => e.pnl > 0), losses = closed.filter(e => e.pnl < 0);
    const avgWin = wins.length ? wins.reduce((s, e) => s + e.pnl, 0) / wins.length : 0;
    const avgLoss = losses.length ? Math.abs(losses.reduce((s, e) => s + e.pnl, 0) / losses.length) : 0;
    let maxDD = 0;
    for (let i = 0; i <= idx && i < prepared.series.length; i++) if (prepared.series[i].dd < maxDD) maxDD = prepared.series[i].dd;
    return {
      cur, closedCount: closed.length, openCount: cur?.open.length || 0,
      wins: wins.length, losses: losses.length,
      winRate: closed.length ? Math.round(wins.length / closed.length * 100) : 0,
      rr: avgLoss ? (avgWin / avgLoss) : (avgWin ? Infinity : 0),
      maxDD,
    };
  }, [prepared, idx, data]);

  // Estimated brokerage + taxes (option selling). Full-range = every trade; live
  // = only trades closed by the current candle (so it accrues as the replay plays).
  const ev = (e) => legCharges(e.entryPrice, e.exitPrice, e.qty, e.side, data?.assetClass);
  const fullCharges = useMemo(
    () => (data ? Math.round((data.events || []).reduce((s, e) => s + ev(e), 0)) : 0),
    [data]);
  const liveCharges = useMemo(() => {
    if (!live || !data) return 0;
    const curKey = live.cur?.key || "";
    return Math.round((data.events || []).filter(e => e.exitKey <= curKey).reduce((s, e) => s + ev(e), 0));
  }, [live, data]);

  // Every position entered up to the current candle — re-entries/retakes are
  // separate rows. Open ones are marked live; closed ones show their exit fill.
  const positionsNow = useMemo(() => {
    if (!prepared || !data) return [];
    const c = prepared.series[Math.min(idx, prepared.series.length - 1)];
    if (!c) return [];
    const curKey = c.key, curTime = c.time, curDate = c.date;
    const rawC = data.candles[Math.min(idx, data.candles.length - 1)];
    const curSpot = rawC ? rawC.c : null; // spot close at the current replay candle
    return data.events
      .filter(e => e.entryKey <= curKey)
      .map(e => {
        const closed = e.exitKey <= curKey;
        let mark, pnl;
        if (closed) { mark = e.exitPrice; pnl = e.pnl; }
        else { mark = markLeg(e, curDate, curTime); pnl = Math.round((e.side === "SELL" ? (e.entryPrice - mark) : (mark - e.entryPrice)) * e.qty); }
        // Entry |Δ| from the engine (fallback: compute from spot+premium at entry).
        const entryDelta = e.entryDelta != null ? e.entryDelta
          : legDeltaAt(e.spotEntry, e.strike, e.optType, e.entryKey.slice(0, 10), e.entryKey.slice(11), e.expiry, e.entryPrice);
        // Mark |Δ|: closed → exit delta; open → LIVE delta at the current candle
        // (ticks as the replay plays; zero lookahead since it uses curSpot + mark).
        const markDelta = closed
          ? (e.exitDelta != null ? e.exitDelta : legDeltaAt(e.spotExit, e.strike, e.optType, e.exitKey.slice(0, 10), e.exitKey.slice(11), e.expiry, e.exitPrice))
          : legDeltaAt(curSpot, e.strike, e.optType, curDate, curTime, e.expiry, mark);
        // "to" timestamp shown next to entry: the exit for closed legs, the CURRENT
        // candle for open ones — so an open position's time ticks forward with the
        // replay. Show just the time when it's the same day as entry, else date+time.
        const toKey = closed ? e.exitKey : curKey;
        const toLabel = toKey.slice(0, 10) === e.entryKey.slice(0, 10) ? toKey.slice(11) : toKey;
        return { symbol: e.symbol, side: e.side, qty: e.qty, entry: e.entryPrice, mark, pnl, status: closed ? "Closed" : "Open", exitType: e.exitType, reentry: e.reentry || 0, entryAt: e.entryKey, toLabel, expiry: e.expiry || null, entryDelta, markDelta };
      })
      // Newest first: most recently entered trade at the top (by entry date+time).
      .sort((a, b) => (a.entryAt < b.entryAt ? 1 : a.entryAt > b.entryAt ? -1 : 0));
  }, [prepared, idx, data]);

  // Realized P&L grouped by the trade's actual CONTRACT EXPIRY (for NIFTY that's
  // the weekly Tuesday). Grouping on the real expiry — not the calendar week —
  // keeps each cycle's legs together even though a new cycle re-enters mid-week.
  // Only counts trades already closed at the current candle, so it fills in as
  // the replay plays (zero lookahead). Falls back to the exit week if the backend
  // didn't stamp an expiry. Each entry: { expiry, pnl }.
  const expiryWeeks = useMemo(() => {
    if (!data || !prepared) return [];
    const cur = prepared.series[Math.min(idx, prepared.series.length - 1)];
    const curKey = cur?.key || "";
    const map = new Map();
    for (const e of data.events) {
      if (e.exitKey > curKey) continue; // not yet closed in the replay
      const expiry = e.expiry || weekStart(e.exitKey.slice(0, 10));
      const g = map.get(expiry) || { expiry, pnl: 0, iv: null };
      g.pnl += e.pnl || 0;
      if (g.iv == null && e.iv != null) g.iv = e.iv; // ATM spot IV for this expiry
      map.set(expiry, g);
    }
    return [...map.values()].sort((a, b) => (a.expiry < b.expiry ? -1 : 1));
  }, [prepared, idx, data]);

  const dates = useMemo(() => (data ? [...new Set(data.candles.map(c => c.date))] : []), [data]);

  const saveSession = () => {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify({ strategyId, range, customFrom, customTo, interval: intervalMin, idx }));
      setSavedSession({ strategyId, range, customFrom, customTo, interval: intervalMin, idx });
    } catch { /* ignore */ }
  };
  const resetSim = () => { setPlaying(false); setIdx(0); };
  const stepNext = () => { setPlaying(false); setIdx(i => Math.min(i + 1, (data?.candles.length || 1) - 1)); };
  const stepPrev = () => { setPlaying(false); setIdx(i => Math.max(i - 1, 0)); };
  const jumpTo = (d) => {
    if (!data) return;
    const i = data.candles.findIndex(c => c.date >= d);
    if (i >= 0) { setPlaying(false); setIdx(i); }
  };

  const cur = prepared?.series[Math.min(idx, (prepared?.series.length || 1) - 1)];
  const atEnd = data && idx >= data.candles.length - 1;

  return (
    <div style={{ padding: "20px 24px", fontFamily: "Inter, sans-serif", background: UI.bg, minHeight: "100vh" }}>
      {/* Hero */}
      <div className="page-hero" style={{ position: "sticky", top: 0, zIndex: 50, background: "linear-gradient(120deg,#4f46e5 0%,#2563eb 55%,#1d4ed8 100%)", borderRadius: 18, padding: "22px 26px", marginBottom: 18, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, boxShadow: "0 14px 34px rgba(37,99,235,0.26)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="hero-icon" style={{ width: 46, height: 46, borderRadius: 13, background: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 23 }}>🎮</div>
          <div>
            <h1 style={{ fontSize: 23, fontWeight: 900, margin: 0, letterSpacing: "-0.4px" }}>Simulated Trading</h1>
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>Replay a saved strategy candle-by-candle on past data — as if it were live</div>
          </div>
        </div>
        {data && (
          <div style={{ textAlign: "right", background: "rgba(255,255,255,0.14)", borderRadius: 12, padding: "10px 18px" }}>
            <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 600, letterSpacing: "0.05em" }}>{data.underlying} · {fmtDate(cur?.date)}</div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{cur?.time} <span style={{ fontSize: 12, opacity: 0.8, fontWeight: 500 }}>· bar {idx + 1}/{data.candles.length}</span></div>
          </div>
        )}
      </div>

      {/* Setup */}
      <div style={card()}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, color: "#1a1a2e" }}>Choose a strategy & range</h2>
        <p style={{ fontSize: 13, color: UI.muted, marginBottom: 16 }}>Runs on downloaded historical data only. Trades are computed by the same engine as Backtesting, then revealed minute-by-minute.</p>
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <select value={strategyId} onChange={e => setStrategyId(e.target.value)} style={{ minWidth: 220, maxWidth: "100%", padding: "9px 12px", border: "1px solid #e8eaf0", borderRadius: 8, fontSize: 13, background: "#fff", color: strategyId ? UI.text : UI.muted }}>
            <option value="">Select a strategy…</option>
            {strategies.map(s => <option key={s._id} value={s._id}>{s.name} ({s.instrument || s.underlying || "—"})</option>)}
          </select>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {RANGES.map(r => (
              <button key={r} onClick={() => setRange(r)} style={{ padding: "8px 13px", borderRadius: 7, border: "1px solid", fontSize: 12, fontWeight: 500, cursor: "pointer", background: range === r ? "#eff6ff" : "#fff", color: range === r ? UI.blue : "#6b7280", borderColor: range === r ? "#93c5fd" : "#e8eaf0" }}>{r}</button>
            ))}
          </div>
          {/* candle size */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11.5, color: UI.muted, fontWeight: 600 }}>Candle</span>
            {[1, 3, 5, 10, 15].map(m => (
              <button key={m} onClick={() => setIntervalMin(m)} title={`${m}-minute candles`}
                style={{ padding: "8px 11px", borderRadius: 7, border: "1px solid", fontSize: 12, fontWeight: 600, cursor: "pointer", background: intervalMin === m ? "#eef2ff" : "#fff", color: intervalMin === m ? UI.indigo || "#4f46e5" : "#6b7280", borderColor: intervalMin === m ? "#c7d2fe" : "#e8eaf0" }}>{m}m</button>
            ))}
          </div>
          <button onClick={() => prepare()} disabled={loading} style={{ background: UI.blue, color: "#fff", border: "none", borderRadius: 8, padding: "9px 22px", fontSize: 13, fontWeight: 700, cursor: loading ? "default" : "pointer", marginLeft: "auto", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Preparing…" : "Prepare Replay"}
          </button>
        </div>
        {range === "Custom Range" && (
          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ padding: "8px 12px", border: "1px solid #e8eaf0", borderRadius: 7, fontSize: 13 }} />
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ padding: "8px 12px", border: "1px solid #e8eaf0", borderRadius: 7, fontSize: 13 }} />
          </div>
        )}
        {savedSession && !data && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: UI.sub }}>
            💾 Saved session found{savedSession.idx ? ` (bar ${savedSession.idx + 1})` : ""}.
            <button onClick={() => prepare(savedSession.idx)} style={{ marginLeft: 8, padding: "4px 10px", fontSize: 12, fontWeight: 600, borderRadius: 6, border: "1px solid #93c5fd", background: "#eff6ff", color: UI.blue, cursor: "pointer" }}>Resume</button>
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: 11.5, color: UI.muted }}>Tip: keep ranges to a few months for a smooth replay (each trading day ≈ 75 five-minute bars).</div>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: 60 }}>
          <div style={{ width: 40, height: 40, border: "3px solid #e8eaf0", borderTop: "3px solid #2563eb", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 14px" }} />
          <div style={{ color: "#6b7280", fontSize: 13 }}>Computing trades & loading candles…</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {error && !loading && (
        <div style={{ display: "flex", gap: 12, padding: "16px 18px", marginBottom: 16, borderRadius: 12, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" }}>
          <span style={{ fontSize: 20, lineHeight: 1 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 800, marginBottom: 4, fontSize: 14 }}>Could not prepare simulation</div>
            <div style={{ fontSize: 13, opacity: 0.95 }}>{error}</div>
          </div>
        </div>
      )}

      {!data && !loading && !error && (
        <div style={{ textAlign: "center", padding: 80, color: UI.muted, fontSize: 14 }}>Select a strategy and click Prepare Replay to begin.</div>
      )}

      {data && prepared && (
        <>
          {data.summary?.notSimulated?.length > 0 && (
            <div style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 10, background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", fontSize: 12.5 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>⚠️ Replay excludes some strategy features</div>
              <div style={{ opacity: 0.9 }}>Not simulated: {data.summary.notSimulated.join(", ")}. Treat the P&L as an optimistic estimate for strategies that rely on them.</div>
            </div>
          )}

          {/* Performance dashboard */}
          <div style={card()}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, color: "#1f2937" }}>Performance Dashboard</div>
            <div>
              <div style={{ fontSize: 12, color: UI.muted, fontWeight: 600, marginBottom: 10 }}>FULL-RANGE STRATEGY RESULT (entire {fmtDate(data.from)} → {fmtDate(data.to)}, same engine as Backtesting)</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14 }}>
                <Metric label="Net P&L (after charges)" value={inr((data.summary.totalPnl || 0) - fullCharges)} color={((data.summary.totalPnl || 0) - fullCharges) >= 0 ? UI.green : UI.red} sub={`Gross ${inr(data.summary.totalPnl)} · Chgs ${inr(fullCharges)}`} />
                <Metric label="Win Rate" value={`${data.summary.winRate}%`} sub={`${data.summary.wins} W / ${data.summary.losses} L`} />
                <Metric label="Max Drawdown" value={inr(data.summary.maxDrawdown)} color={UI.red} />
                <Metric label="Trading Days" value={data.summary.tradingDays} />
                <Metric label="Sharpe" value={data.summary.sharpe} />
              </div>
              <div style={{ fontSize: 11.5, color: UI.muted, marginTop: 10 }}>
                Charges (est.): ₹20/order brokerage · 0.125% STT (sell) · 0.032% exchange · 18% GST · 0.003% stamp — calibrated to a real Zerodha statement (June 2026); actual varies by broker.
                {data.summary.dataMode ? ` · ${data.summary.dataMode}${data.summary.skippedNoData > 0 ? ` · ${data.summary.skippedNoData} day(s) skipped (no real option data)` : ""}` : ""}
              </div>
            </div>
          </div>

          {/* Live P&L strip */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 16 }}>
            <Metric label="Net P&L (live, after charges)" value={inr((live.cur?.pnl || 0) - liveCharges)} color={((live.cur?.pnl || 0) - liveCharges) >= 0 ? UI.green : UI.red} sub={`Gross ${inr(live.cur?.pnl)} · Chgs ${inr(liveCharges)} · Open ${inr(live.cur?.openMtm)}`} />
            <Metric label="Drawdown (so far)" value={inr(live.maxDD)} color={UI.red} />
            <Metric label="Win Rate (closed legs)" value={`${live.winRate}%`} color={UI.blue} sub={`${live.wins} W / ${live.losses} L`} />
            <Metric label="Trades" value={`${live.closedCount} closed`} sub={`${live.openCount} open now`} />
          </div>

          {/* Replay controls */}
          <div style={card({ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" })}>
            <button onClick={() => { if (atEnd) setIdx(0); setPlaying(p => !p); }} style={{ background: playing ? "#fee2e2" : UI.blue, color: playing ? "#991b1b" : "#fff", border: "none", borderRadius: 8, padding: "9px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer", minWidth: 96 }}>
              {playing ? "⏸ Pause" : atEnd ? "↺ Replay" : "▶ Play"}
            </button>
            <button onClick={stepPrev} disabled={idx <= 0} style={{ border: "1px solid #e8eaf0", background: "#fff", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: idx <= 0 ? "default" : "pointer", color: UI.sub, opacity: idx <= 0 ? 0.5 : 1 }}>⏮ Back</button>
            <button onClick={stepNext} disabled={atEnd} style={{ border: "1px solid #e8eaf0", background: "#fff", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: atEnd ? "default" : "pointer", color: UI.sub, opacity: atEnd ? 0.5 : 1 }}>⏭ Next</button>
            <button onClick={resetSim} style={{ border: "1px solid #e8eaf0", background: "#fff", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: UI.sub }}>↺ Reset</button>
            <div style={{ display: "flex", gap: 4, marginLeft: 4 }}>
              {SPEEDS.map(([lbl, ms]) => (
                <button key={ms} onClick={() => setSpeed(ms)} style={{ padding: "8px 12px", borderRadius: 7, border: "1px solid", fontSize: 12, fontWeight: 600, cursor: "pointer", background: speed === ms ? "#eff6ff" : "#fff", color: speed === ms ? UI.blue : "#6b7280", borderColor: speed === ms ? "#93c5fd" : "#e8eaf0" }}>{lbl}</button>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }}>
              <span style={{ fontSize: 12, color: UI.muted }}>Jump:</span>
              <select value={jumpDate} onChange={e => { setJumpDate(e.target.value); jumpTo(e.target.value); }} style={{ padding: "7px 10px", border: "1px solid #e8eaf0", borderRadius: 7, fontSize: 12.5 }}>
                {dates.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <button onClick={saveSession} style={{ marginLeft: "auto", border: "1px solid #e8eaf0", background: "#fff", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: UI.sub }}>💾 Save session</button>
          </div>

          {/* Scrubber */}
          <div style={card({ paddingTop: 14, paddingBottom: 14 })}>
            <input type="range" min={0} max={data.candles.length - 1} value={idx} onChange={e => { setPlaying(false); setIdx(Number(e.target.value)); }} style={{ width: "100%" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: UI.muted, marginTop: 2 }}>
              <span>{fmtDate(data.candles[0]?.date)} {data.candles[0]?.time}</span>
              <span>{fmtDate(cur?.date)} {cur?.time}</span>
              <span>{fmtDate(data.candles[data.candles.length - 1]?.date)}</span>
            </div>
          </div>

          {/* Candle chart (left) + Positions (right) — 50/50 */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "stretch" }}>
            {/* Candle chart */}
            <div style={card({ marginBottom: 0 })}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}>{data.underlying} · 5-min</div>
                <div style={{ fontSize: 12, color: UI.sub }}>
                  <span style={{ color: UI.green }}>▲ entry</span> &nbsp; <span style={{ color: "#7c3aed" }}>▼ exit</span> &nbsp;·&nbsp; close <strong>{Math.round((data.candles[idx]?.c) || 0).toLocaleString()}</strong>
                </div>
              </div>
              <CandleChart candles={data.candles} idx={idx} entryMap={prepared.entryMap} exitMap={prepared.exitMap} />
            </div>

            {/* Positions — all entered so far, open + closed */}
            <div style={card({ marginBottom: 0 })}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: "#1f2937" }}>
                Positions <span style={{ fontSize: 12, color: UI.muted, fontWeight: 500 }}>({live.openCount} open · {live.closedCount} closed)</span>
              </div>
              {positionsNow.length ? (
                <div className="ar-tablewrap" style={{ maxHeight: 360, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>{["Symbol", "Side", "Qty", "Entry → Mark", "Delta", "P&L", "Status"].map(h => <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, color: UI.muted, fontWeight: 700, borderBottom: "1px solid #f1f3f9", position: "sticky", top: 0, background: "#fff" }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {positionsNow.map((p, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f9fafb", background: p.status === "Open" ? "#fbfdff" : "transparent" }}>
                          <td style={{ padding: "9px 10px", fontSize: 12.5, fontWeight: 600 }}>
                            {formatOptionSymbol(p.symbol)}
                            {p.reentry > 0 && <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 5, fontSize: 10, fontWeight: 700, background: "#fef3c7", color: "#92400e" }}>↻ #{p.reentry}</span>}
                            <div style={{ fontSize: 10.5, color: UI.muted, fontWeight: 500, marginTop: 2 }}>
                              🕒 {p.entryAt} <span style={{ color: UI.muted }}>→</span> {p.toLabel}
                              {p.status === "Open" && <span style={{ marginLeft: 5, color: UI.blue, fontWeight: 700 }}>· now</span>}
                            </div>
                          </td>
                          <td style={{ padding: "9px 10px", fontSize: 12 }}><span style={{ padding: "2px 7px", borderRadius: 5, fontWeight: 700, background: p.side === "SELL" ? "#fee2e2" : "#dcfce7", color: p.side === "SELL" ? "#dc2626" : "#16a34a" }}>{p.side}</span></td>
                          <td style={{ padding: "9px 10px", fontSize: 12.5, fontWeight: 700, color: p.side === "SELL" ? "#dc2626" : "#16a34a" }}>{p.side === "SELL" ? "−" : "+"}{p.qty}</td>
                          <td style={{ padding: "9px 10px", fontSize: 12.5 }}>{p.entry} <span style={{ color: UI.muted }}>→</span> {p.mark}</td>
                          <td style={{ padding: "9px 10px", fontSize: 12.5 }} title="Black-Scholes |Δ| at entry → current/exit (IV implied from the leg's own premium). Open legs tick live as you scrub.">
                            <span style={{ fontWeight: 700, color: p.entryDelta != null && p.entryDelta >= 0.30 ? "#b91c1c" : "#0f172a" }}>{p.entryDelta != null ? "Δ" + p.entryDelta.toFixed(2) : "—"}</span>
                            <span style={{ color: UI.muted }}> → </span>
                            <span style={{ fontWeight: 700, color: p.markDelta != null && p.markDelta >= 0.30 ? "#dc2626" : (p.status === "Open" ? UI.blue : UI.muted) }}>{p.markDelta != null ? p.markDelta.toFixed(2) : "—"}</span>
                          </td>
                          <td style={{ padding: "9px 10px", fontSize: 12.5, fontWeight: 700, color: p.pnl >= 0 ? UI.green : UI.red }}>{inr(p.pnl)}</td>
                          <td style={{ padding: "9px 10px", fontSize: 12 }}>
                            <span title={p.status === "Closed" ? (exitReason(p.exitType)?.label || "Closed") : "Currently open"} style={{ padding: "2px 8px", borderRadius: 5, fontWeight: 700, background: p.status === "Open" ? "#dbeafe" : "#f1f5f9", color: p.status === "Open" ? "#1e40af" : "#475569" }}>{p.status}</span>
                            {p.status === "Closed" && (() => {
                              const r = exitReason(p.exitType);
                              return r ? <div style={{ marginTop: 4 }}><span style={{ padding: "1px 7px", borderRadius: 5, fontSize: 10.5, fontWeight: 700, background: r.bg, color: r.fg }}>{r.label}</span></div> : null;
                            })()}
                            {p.status === "Closed" && p.expiry && <div style={{ fontSize: 10.5, color: UI.muted, fontWeight: 500, marginTop: 3 }}>Exp: {fmtDate(p.expiry)}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <div style={{ color: UI.muted, fontSize: 13, padding: "8px 0" }}>No positions entered yet — play the replay forward.</div>}
            </div>
          </div>

          {/* Expiry P&L — monthly calendar. Full width + wraps, so it never runs
              off the screen. Each expiry day (NIFTY = Tuesday) shows its P&L. */}
          <div style={card({ marginTop: 16, marginBottom: 0 })}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: "#1f2937" }}>
              Expiry P&amp;L <span style={{ fontSize: 12, color: UI.muted, fontWeight: 500 }}>· by expiry day, fills in as the replay closes each week</span>
            </div>
            <ExpiryCalendar weeks={expiryWeeks} />
          </div>

        </>
      )}
    </div>
  );
}
